import React, { useState } from 'react';
import { ToastError } from 'components/Toaster';

export type ProgressStatus = 'idle' | 'creating' | 'created' | 'uploading' | 'success' | 'error';

export interface ProgressConfig {
    status: ProgressStatus;
    message: string;
    isSubmitting: boolean;
}

export interface UseSubmitProgressReturn {
    config: ProgressConfig;
    setCreating: (message?: string) => void;
    setCreated: () => void;
    setUploading: (message?: string) => void;
    setSuccess: (message?: string) => void;
    setError: (message: string) => void;
    reset: () => void;
    ProgressComponent: React.FC;
}

const defaultMessages = {
    creating: 'Criando item...',
    created: '✓ Item criado com sucesso',
    uploading: 'Enviando arquivo...',
    success: '✓ Salvo com sucesso',
    error: 'Erro',
};

export const useSubmitProgress = (): UseSubmitProgressReturn => {
    const [status, setStatus] = useState<ProgressStatus>('idle');
    const [message, setMessage] = useState<string>('');

    const reset = () => {
        setStatus('idle');
        setMessage('');
    };

    const setCreating = (customMessage?: string) => {
        setStatus('creating');
        setMessage(customMessage || defaultMessages.creating);
    };

    const setCreated = () => {
        setStatus('created');
        setMessage(defaultMessages.created);
    };

    const setUploading = (customMessage?: string) => {
        setStatus('uploading');
        setMessage(customMessage || defaultMessages.uploading);
    };

    const setSuccess = (customMessage?: string) => {
        setStatus('success');
        setMessage(customMessage || defaultMessages.success);
    };

    const setError = (errorMessage: string) => {
        setStatus('error');
        setMessage(`Erro: ${errorMessage}`);
        ToastError(errorMessage);
    };

    const ProgressComponent: React.FC = () => {
        if (!message) return null;

        return (
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: '10px',
                    padding: '12px 16px',
                    borderRadius: '4px',
                    backgroundColor:
                        status === 'error'
                            ? '#fff2f0'
                            : status === 'success'
                              ? '#f6ffed'
                              : '#e6f7ff',
                    border:
                        status === 'error'
                            ? '1px solid #ffccc7'
                            : status === 'success'
                              ? '1px solid #b7eb8f'
                              : '1px solid #91d5ff',
                    color:
                        status === 'error'
                            ? '#ff4d4f'
                            : status === 'success'
                              ? '#52c41a'
                              : '#0050b3',
                    fontSize: '14px',
                    fontWeight: '500',
                }}
            >
                {status !== 'error' && status !== 'success' && (
                    <div
                        style={{
                            display: 'inline-block',
                            width: '14px',
                            height: '14px',
                            marginRight: '8px',
                            border: '2px solid currentColor',
                            borderRadius: '50%',
                            borderTop: '2px solid transparent',
                            animation: 'spin 1s linear infinite',
                        }}
                    />
                )}
                {message}
                <style>{`
                    @keyframes spin {
                        to { transform: rotate(360deg); }
                    }
                `}</style>
            </div>
        );
    };

    return {
        config: {
            status,
            message,
            isSubmitting: status !== 'idle' && status !== 'error' && status !== 'success',
        },
        setCreating,
        setCreated,
        setUploading,
        setSuccess,
        setError,
        reset,
        ProgressComponent,
    };
};
